import React, { useEffect, useState, useRef } from 'react';
import { Form } from 'react-bootstrap-5';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/unstyled_dropdown_toggle';
import Dropdown from 'components/dropdown';
import { MenuItem } from 'react-bootstrap';
import { post } from 'helpers/api';

import Modal from 'components/modal';
import Button from 'shared/button';

import {
  faPaperPlane,
  faPlus,
  faTimes,
  faChevronLeft,
  faChevronDown,
  faSpinner
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  formatDateTime,
  shareClientReport,
  removeShareableToken,
  type ShareableToken,
  type Collaborator
} from './util';

type ShareClientReportModalProps = {
  collaborators: Collaborator[];
  projectId: number;
  shareableTokens: ShareableToken[];
  show: boolean;
  showAddEmail: boolean;
  onCloseModal: () => void;
  onTokensChanged?: () => void;
}

function ShareClientReportModal({
  collaborators,
  projectId,
  shareableTokens,
  show,
  showAddEmail,
  onCloseModal,
  onTokensChanged // Add this to the function params
}: ShareClientReportModalProps) {

  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendingTokens, setResendingTokens] = useState<Set<number>>(new Set());
  const [recentlySent, setRecentlySent] = useState<Set<number>>(new Set());

  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

  useEffect(() => {
    return () => {
      // Clear all timeouts on unmount to prevent memory leaks
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current.clear();
    };
  }, []);

  const [selectedCollaborator, setSelectedCollaborator] = useState(
    collaborators?.length > 0 ? collaborators[0].id : ''
  );
  const [selectedCollaboratorName, setSelectedCollaboratorName] = useState(
    collaborators?.length > 0 ? collaborators[0].name : ''
  );

  useEffect(() => {
    if (collaborators?.length > 0 && !selectedCollaborator) {
      setSelectedCollaborator(collaborators[0].id.toString());
      setSelectedCollaboratorName(collaborators[0].name);
    }
  }, [collaborators]);

  const handleCollaboratorSelect = (collabId: number, collabName: string) => {
    setSelectedCollaborator(collabId);
    setSelectedCollaboratorName(collabName);
  };

  const sendShareClientReport = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await shareClientReport(
        projectId,
        selectedCollaborator,
        email,
        message
      );

      setEmail('');
      setMessage('');
      setIsAddingEmail(false);

      if (onTokensChanged) {
        onTokensChanged();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveToken = async (e, tokenId: number) => {
    e.preventDefault();

    try {
      await removeShareableToken(projectId, tokenId);

      if (onTokensChanged) {
        onTokensChanged();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const downloadPDF = async () => {
    const pdfPath = `client_report`;
    const data = await post(pdfPath);
    window.location.assign(data.editPath);
  };

  const handleResendToken = async (e, token: ShareableToken) => {
    e.preventDefault();

    if (resendingTokens.has(token.id)) {
      return;
    }

    setResendingTokens(prev => new Set(prev).add(token.id));

    try {
      await shareClientReport(
        projectId,
        collaborators[0].id,
        token.email,
        ''
      );

      setRecentlySent(prev => new Set(prev).add(token.id));

      const timeOutId = setTimeout(() => {
        setRecentlySent((prev) => {
          const newSet = new Set(prev);
          newSet.delete(token.id);
          return newSet;
        });
        timeoutRefs.current.delete(timeOutId);
      }, 3000);
      
      timeoutRefs.current.add(timeOutId);

      if (onTokensChanged) {
        onTokensChanged();
      }
    } catch (error) {
      console.error('Failed to resend:', error);
    } finally {
      setResendingTokens((prev)  => {
        const newSet = new Set(prev);
        newSet.delete(token.id);
        return newSet;
      });
    }
  };

  const isTokenDisabled = (tokenId: number) => {
    return resendingTokens.has(tokenId) || recentlySent.has(tokenId);
  };

  const renderResendStatus = (token: ShareableToken) => {
    const isResending = resendingTokens.has(token.id);
    const wasRecentlySent = recentlySent.has(token.id);

    if (isResending) {
      return (
        <>
          <FontAwesomeIcon className="ml-2" icon={faSpinner} spin />
          Sending...
        </>
      );
    }

    if (wasRecentlySent) {
      return (
        <span className="ml-2 text-success">
          Sent successfully!
        </span>
      );
    }

    return (
      <a
        className="clickable btn-link ml-2"
        onClick={isTokenDisabled(token.id) ? undefined : e => handleResendToken(e, token)}
      >
        Resend
      </a>
    );
  };

  const renderTokenEmail = (token: ShareableToken) => {
    if (!token.enabled) {
      return null;
    }

    return (
      <div className="mb-4 w-100 px-5" key={token.id} >
        <h4 className="mb-2 fw-bold">Email Address</h4>
        <div className="d-flex align-items-center w-100">
          <Form.Control
            className="flex-grow-1 me-3"
            readOnly
            type="email"
            value={token.email || ''}
          />
          <Button.Danger className="ds-button--plain ml-2" onClick={e => handleRemoveToken(e, token.id)}>
            <FontAwesomeIcon className="me-1" icon={faTimes}/>
            <span className="mx-2">Remove</span>
          </Button.Danger>
        </div>
        <div className="mt-1 d-flex align-items-center">
          <div className="text-muted d-flex align-items-center">
            Last Sent At: {token.lastSent ? formatDateTime(token.lastSent) : 'Not sent yet'}
          </div>
          {renderResendStatus(token)}
        </div>
      </div>
    );
  };

  const renderAddEmailForm = () => {
    return (
      <div className="w-100 p-4">
        <div className="mb-4">
          <Button.Secondary className="ds-button--plain" onClick={() => setIsAddingEmail(false)}>
            <FontAwesomeIcon className="me-2" icon={faChevronLeft} />
            <span className="mx-2">Back</span>
          </Button.Secondary>
        </div>

        <Form onSubmit={sendShareClientReport}>
          <div className="row mb-4">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-bold">Email Address</Form.Label>
                <Form.Control
                  placeholder="Enter Email Address"
                  required
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="form-group-wrapper">
                <Form.Label className="fw-bold">From</Form.Label>
                <div className="dropdown-wrapper">
                  <Dropdown className="w-100" id="collaborator-dropdown">
                    <UnstyledDropdownToggle
                      bsRole="toggle"
                      className="collaborator-dropdown__toggle ds-button ds-button--outlined w-100 d-flex justify-content-between align-items-center"
                    >
                      <span>{selectedCollaboratorName}</span>
                      <FontAwesomeIcon className="ms-2" icon={faChevronDown} />
                    </UnstyledDropdownToggle>
                    <Dropdown.Menu className="collaborator-dropdown__menu">
                      {collaborators.map(collab => (
                        <MenuItem
                          className="collaborator-dropdown__item"
                          key={collab.id}
                          onClick={() => handleCollaboratorSelect(collab.id, collab.name)}
                        >
                          <div className="d-flex align-items-center">
                            <span>{collab.name}</span>
                          </div>
                        </MenuItem>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Message</Form.Label>
            <Form.Control
              as="textarea"
              placeholder="Message"
              rows={6}
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <Button.Cancel
              className="ds-button--outlined mx-3"
              type="button"
              onClick={() => setIsAddingEmail(false)}
            >
              Cancel
            </Button.Cancel>
            <Button.Primary
              disabled={!email || isSubmitting}
              type="submit"
            >
              Share
            </Button.Primary>
          </div>
        </Form>
      </div>
    );
  };

  const renderShareView = () => {
    return (
      <>
        <Modal.Body className="d-flex flex-column align-items-center">
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex flex-row m-1 align-items-center">
              <FontAwesomeIcon className="mx-2 mt-2" icon={faPaperPlane} size="lg"/>
              <h3>Share the Report</h3>
            </div>
            <h4 className="fw-normal text-muted">Send anyone an invitation to view your Client Report.</h4>
          </div>
          <div className="body d-flex flex-column align-items-center w-100">
            {(shareableTokens || []).map(token => renderTokenEmail(token))}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-row justify-content-between w-100">
          <div className="pl-5">
            {showAddEmail && <Button.Secondary className="ds-button--plain" onClick={() => setIsAddingEmail(true)}>
              <FontAwesomeIcon icon={faPlus}/>
              <span className="mx-2">Add Email</span>
            </Button.Secondary>}
          </div>
          <div className="pr-5">
            <Button.Primary onClick={downloadPDF}>
              Download PDF
            </Button.Primary>
          </div>
        </Modal.Footer>
      </>
    );
  };

  const renderAddEmailView = () => {
    return (
      <Modal.Body>
        {renderAddEmailForm()}
      </Modal.Body>
    );
  };

  return (
    <Modal className="share-client-report-modal" show={show} size={isAddingEmail ? 'lg' : 'md'} onHide={onCloseModal}>
      {isAddingEmail ? renderAddEmailView() : renderShareView()}
    </Modal>
  );
}

export default ShareClientReportModal;